import { useEffect } from 'react';
import {
  useClientConfig,
  useLocalForageContext,
  useAuthContext,
} from '../context';
import { useApolloClient } from '@apollo/react-hooks';
import moment from 'moment';
import localforage from 'localforage';
import { getProductsData } from '@msales/shared/graphql/queries/product/getProductsData';
import { getProductsDataUnrestricted } from '@msales/shared/graphql/queries/product/getProductsDataUnrestricted';
import { useInsertEventLog } from './useInsertEventLog';
import { useOfflineModeContext } from '../context/offlineContext';
import { notificationManager } from '../utils/notificationManager';
import { smartDataManager } from '../utils/smartDataManager';
import { categoryLookup } from '../utils/categoryLookup';
import { reportSyncError, reportSyncRecovery } from '../utils/sentryHelpers';
import { compressedStorage } from '../utils/compressedStorage';
import { safeSetItem } from '../utils/storageQuotaGuard';

export const useProductsOfflineData = () => {
  const {
    loadingProducts: loading,
    setLoadingProducts: setLoading,
    refetchProducts: refetch,
    setRefetchProducts: setRefetch,
  } = useLocalForageContext();
  const { authUser } = useAuthContext();
  const { getValue } = useClientConfig();
  const { insertEvent } = useInsertEventLog();

  const productsStore = localforage.createInstance({
    name: 'Products',
  });
  const { networkState } = useOfflineModeContext();
  const apolloClient = useApolloClient();

  useEffect(() => {
    const FetchProcess = async () => {
      const shouldAsync = async (refetch) => {
        try {
          const createdAt = await productsStore.getItem('createdAt');

          if (createdAt && !refetch) {
            const meta = await productsStore.getItem('products_meta');
            const hasCatalog =
              meta?.count > 0 || Boolean(await productsStore.getItem('products'));
            if (!hasCatalog) {
              await productsStore.removeItem('createdAt');
              console.warn('[AsyncProducts] No product data found, forcing re-sync');
              reportSyncRecovery('products', 'createdAt-without-data');
            }
          }

          const freshCreatedAt = await productsStore.getItem('createdAt');
          if (
            (refetch ||
              !freshCreatedAt ||
              (false &&
                moment().diff(
                  moment(freshCreatedAt, 'DD/MM/YYYY HH:mm:ss'),
                  'minutes',
                ) >= 30)) &&
            (!networkState || networkState.online) &&
            authUser &&
            !loading
          ) {
            return true;
          }
          if (refetch) setRefetch(false);
          return false;
        } catch (err) {
          console.error('Error in shouldAsync:', err);
          return false;
        }
      };

      const AsyncProducts = async () => {
        try {
          setLoading(true);
          notificationManager.startSync('products', 'Artikli', {
            warning: 'Moguća privremena usporenja',
          });

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 90000);

          try {
            const isUnrestrictedClient = getValue(
              'productCatalog.useUnrestrictedProductsQuery',
              false,
            );
            const query = isUnrestrictedClient
              ? getProductsDataUnrestricted
              : getProductsData;

            const { data, errors } = await apolloClient.query({
              query,
              fetchPolicy: 'no-cache',
              errorPolicy: 'all',
              context: { fetchOptions: { signal: controller.signal } },
            });
            clearTimeout(timeoutId);

            if (errors && errors.length > 0) {
              console.error('GraphQL errors in AsyncProducts:', errors);
              const errorMessages = errors.map((err) => err.message).join(', ');
              throw new Error(`GraphQL errors: ${errorMessages}`);
            }

            const productsData =
              data?.productsAll || data?.productsAllUnrestricted;

            await compressedStorage.save(
              productsStore,
              'products',
              productsData,
            );

            const posCount = Array.isArray(productsData)
              ? productsData.filter(p => p.isPos).length
              : 0;
            await safeSetItem(productsStore, 'products_meta', {
              count: productsData?.length || 0,
              posCount,
            });

            const criticalWrites = await Promise.all([
              safeSetItem(productsStore, 'customersCategories', data?.customersCategories),
              safeSetItem(productsStore, 'categories', data?.hierarchicalCategories || data?.hierarchicalCategoriesUnrestricted),
              safeSetItem(productsStore, 'productStocks', data?.productStocks || data?.productStocksUnrestricted),
              safeSetItem(productsStore, 'productGroups', data?.productGroups),
              safeSetItem(productsStore, 'deliveryHistory', data?.productDeliveryHistoryByUser),
              safeSetItem(productsStore, 'productSpecCategories', data?.productSpecCategories),
              data?.suppliers ? safeSetItem(productsStore, 'suppliers', data.suppliers) : Promise.resolve(true),
            ]);

            if (criticalWrites.some(ok => ok === false)) {
              throw new Error('Nema dovoljno prostora za čuvanje podataka artikala. Pokušajte obrisati neke fajlove sa uređaja.');
            }

            await productsStore.setItem(
              'createdAt',
              moment().format('DD/MM/YYYY HH:mm:ss'),
            );

            smartDataManager.setProductsCache(productsData);
            smartDataManager.invalidateFilterResultsCache();

            await categoryLookup.refresh();

            insertEvent({
              eventCode: 'SYNC',
              remark: 'Artikli',
            });

            notificationManager.completeSync('products', true);
            smartDataManager.notifyIndexStatus(false);
            setRefetch(false);
          } catch (err) {
            clearTimeout(timeoutId);
            console.error('Error in AsyncProducts:', err);
            reportSyncError(err, 'products', networkState);

            let errorMessage = 'Greška prilikom učitavanja artikala!';
            if (err.name === 'AbortError' || controller.signal.aborted) {
              errorMessage = 'Sinhronizacija artikala trajala predugo. Provjerite konekciju i pokušajte ponovo.';
            } else if (err.networkError?.statusCode === 400) {
              errorMessage = 'Neispravni podaci za učitavanje artikala';
            } else if (err.networkError?.statusCode >= 500) {
              errorMessage = 'Greška na serveru prilikom učitavanja artikala';
            } else if (err.message?.includes('Firebase ID token')) {
              errorMessage = 'Greška autentifikacije';
            } else if (err.message) {
              errorMessage = err.message;
            }

            notificationManager.completeSync('products', false, errorMessage);
            smartDataManager.notifyIndexStatus(false);
            setRefetch(false);
          }
        } finally {
          setLoading(false);
        }
      };

      const callback = async (refetch) => {
        try {
          const should = await shouldAsync(refetch);
          if (should) await AsyncProducts();
          else if (authUser) await categoryLookup.initialize();
        } catch (err) {
          console.error('Error in callback:', err);
          setLoading(false);
        }
      };

      try {
        await callback(refetch);
        // setInterval(() => callback(refetch), 180000);
      } catch (err) {
        console.error('Error in FetchProcess:', err);
      }
    };

    FetchProcess();

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, authUser]);

  return null;
};
